import * as R from 'ramda';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
import { ENUMS } from '../../../constants/enums';
// feature driver-cards-v2
import { isRouteBuilderLoad } from '../edit-locks';
import {
  RESOURCE_MODE_TRUCK,
  LEGACY_TIMELINE_EVENTS_FIELD,
  TIMELINE_EVENT_KIND_DELIVERY,
  TIMELINE_EVENT_SCHEDULED_DATE_FIELD,
} from '../constants';
//////////////////////////////////////////////////

const getArrayPayload = (data) => {
  if (Array.isArray(data)) return data;

  const results = R.prop('results', data);

  if (Array.isArray(results)) return results;

  const elements = R.prop('elements', data);

  return Array.isArray(elements) ? elements : [];
};

const getLoadDate = (load, eventName, eventDateNames, fallbackNames) => {
  const eventDate = eventDateNames.reduce((date, name) => (
    date || R.path([eventName, name], load)
  ), null);

  if (eventDate) return eventDate;

  return fallbackNames.reduce((date, name) => date || R.prop(name, load), null);
};

const getTimelineEventKind = (event) => {
  const eventType = R.prop(GC.FIELD_EVENT_TYPE, event);
  const typeText = String(eventType || R.prop(GC.FIELD_TYPE, event) || '').toUpperCase();

  return G.isEventTypePickup(eventType) || typeText.includes(GC.EVENT_TYPE_PICKUP)
    ? GC.EVENT_TYPE_PICKUP
    : TIMELINE_EVENT_KIND_DELIVERY;
};

const getTimelineEventDate = (event, kind) => {
  const dateFields = kind === GC.EVENT_TYPE_PICKUP
    ? [
      GC.FIELD_LOAD_EVENT_EARLY_DATE,
      GC.FIELD_EARLY_DATE,
      GC.FIELD_LOAD_EVENT_LATE_DATE,
      GC.FIELD_LATE_DATE,
      GC.FIELD_START_DATE,
      GC.FIELD_DATE_TIME,
      TIMELINE_EVENT_SCHEDULED_DATE_FIELD,
      GC.FIELD_DATE,
    ]
    : [
      GC.FIELD_LOAD_EVENT_LATE_DATE,
      GC.FIELD_LATE_DATE,
      GC.FIELD_LOAD_EVENT_EARLY_DATE,
      GC.FIELD_EARLY_DATE,
      GC.FIELD_END_DATE,
      GC.FIELD_DATE_TIME,
      TIMELINE_EVENT_SCHEDULED_DATE_FIELD,
      GC.FIELD_DATE,
    ];

  return dateFields.reduce((date, field) => date || R.prop(field, event), null);
};

const getRawTimelineEvents = (load) => (
  [GC.FIELD_LOAD_STOPS, LEGACY_TIMELINE_EVENTS_FIELD, GC.FIELD_STOPS]
    .map((field) => R.prop(field, load))
    .find((items) => Array.isArray(items) && items.length) || []
);

const getEventId = (event) => G.getGuidFromObject(event) || R.prop(GC.FIELD_ID, event);

const sortCanonicalEvents = (events) => {
  const indexes = events.map(({ source }) => {
    const value = R.prop(GC.FIELD_TEL_EVENT_INDEX, source);

    return G.isNilOrEmpty(value) ? Number.NaN : Number(value);
  });

  const useEventIndexes = indexes.every(Number.isFinite) && new Set(indexes).size === indexes.length;

  return [...events].sort((left, right) => {
    if (useEventIndexes) {
      return Number(R.prop(GC.FIELD_TEL_EVENT_INDEX, left.source))
        - Number(R.prop(GC.FIELD_TEL_EVENT_INDEX, right.source));
    }

    const difference = new Date(left.at).getTime() - new Date(right.at).getTime();

    return difference || left.sourceIndex - right.sourceIndex;
  });
};

const findEndpointIndex = (events, event, kind, fromEnd = false) => {
  const eventId = getEventId(event);

  const identityIndex = eventId
    ? events.findIndex((candidate) => candidate.id === eventId && candidate.kind === kind)
    : -1;

  if (identityIndex >= 0) return identityIndex;

  if (!fromEnd) return events.findIndex((candidate) => candidate.kind === kind);

  return events.reduce((result, candidate, index) => (
    candidate.kind === kind ? index : result
  ), -1);
};

const getDropdownGuid = (value) => {
  if (!value || typeof value !== 'object') return value;

  return R.prop(GC.FIELD_DROPDOWN_OPTION_GUID, value)
    || R.prop(GC.FIELD_PARENT_GUID, value)
    || G.getGuidFromObject(value)
    || R.prop(GC.FIELD_STORED_VALUE, value);
};

const normalizeTimelineEvents = (load, loadId, startAt, endAt) => {
  const rawEvents = getRawTimelineEvents(load);

  let events = rawEvents.map((event, index) => {
    const kind = getTimelineEventKind(event);

    return {
      kind,
      source: event,
      sourceIndex: index,
      at: getTimelineEventDate(event, kind),
      location: R.prop(GC.FIELD_LOCATION, event),
      id: G.getGuidFromObject(event) || R.prop(GC.FIELD_ID, event) || `${loadId}:${kind}:${index}`,
    };
  }).filter(({ at }) => at);

  events = sortCanonicalEvents(events);

  let firstPickupIndex = findEndpointIndex(events, load.firstEvent, GC.EVENT_TYPE_PICKUP);

  if (firstPickupIndex < 0) {
    events.unshift({
      at: startAt,
      sourceIndex: -1,
      kind: GC.EVENT_TYPE_PICKUP,
      source: R.prop(GC.SYSTEM_OBJECT_FIRST_EVENT, load),
      id: getEventId(load.firstEvent) || `${loadId}:${GC.EVENT_TYPE_PICKUP}:0`,
      location: R.path([GC.SYSTEM_OBJECT_FIRST_EVENT, GC.FIELD_LOCATION], load),
    });
    firstPickupIndex = 0;
  }

  let finalDeliveryIndex = findEndpointIndex(events, load.lastEvent, TIMELINE_EVENT_KIND_DELIVERY, true);

  if (finalDeliveryIndex < 0) {
    events.push({
      at: endAt,
      sourceIndex: rawEvents.length,
      kind: TIMELINE_EVENT_KIND_DELIVERY,
      source: R.prop(GC.SYSTEM_OBJECT_LAST_EVENT, load),
      location: R.path([GC.SYSTEM_OBJECT_LAST_EVENT, GC.FIELD_LOCATION], load),
      id: getEventId(load.lastEvent) || `${loadId}:${TIMELINE_EVENT_KIND_DELIVERY}:0`,
    });
    finalDeliveryIndex = events.length - 1;
  }

  events[firstPickupIndex] = {
    ...events[firstPickupIndex],
    at: startAt,
    source: { ...load.firstEvent, ...events[firstPickupIndex].source },
  };
  events[finalDeliveryIndex] = {
    ...events[finalDeliveryIndex],
    at: endAt,
    source: { ...load.lastEvent, ...events[finalDeliveryIndex].source },
  };

  events = sortCanonicalEvents(events);

  const counters = {
    [GC.EVENT_TYPE_PICKUP]: 0,
    [TIMELINE_EVENT_KIND_DELIVERY]: 0,
  };

  return events.map(({ sourceIndex, ...event }) => {
    counters[event.kind] += 1;

    return { ...event, sequence: counters[event.kind] };
  });
};

export const getTimelineLoadTelGuid = (load = {}) => {
  const source = R.prop(GC.FIELD_SOURCE, load);

  return R.prop(GC.FIELD_TEL_GUID, load)
    || G.getGuidFromObject(source)
    || R.prop(GC.FIELD_ID, source)
    || G.getGuidFromObject(load)
    || (source ? null : R.prop(GC.FIELD_ID, load))
    || null;
};

export const normalizeLoad = (load) => {
  const endAt = getLoadDate(
    load,
    GC.SYSTEM_OBJECT_LAST_EVENT,
    [GC.FIELD_LOAD_EVENT_LATE_DATE, GC.FIELD_LATE_DATE],
    ['lastEventLateDate', GC.FIELD_END_DATE, GC.FIELD_LOAD_BOARD_DELIVERY_DATE],
  );

  const startAt = getLoadDate(
    load,
    GC.SYSTEM_OBJECT_FIRST_EVENT,
    [GC.FIELD_LOAD_EVENT_EARLY_DATE, GC.FIELD_EARLY_DATE],
    ['firstEventEarlyDate', GC.FIELD_START_DATE, GC.FIELD_LOAD_BOARD_PICKUP_DATE],
  );

  const reference = R.prop(GC.FIELD_PRIMARY_REFERENCE_VALUE, load)
    || R.prop(GC.FIELD_REFERENCE_VALUE, load)
    || R.prop('loadReference', load);

  const telGuid = getTimelineLoadTelGuid(load);
  const id = telGuid || `${reference}:${startAt}:${endAt}`;
  const closNames = R.pathOr([], [GC.FIELD_CLOS], load).map(({ branchName }) => branchName).filter(Boolean);

  const branchName = R.path(['enterprise', GC.FIELD_BRANCH_NAME], load)
    || R.prop(GC.FIELD_ENTERPRISE_NAME, load);

  return {
    id,
    endAt,
    startAt,
    telGuid,
    reference,
    branchName,
    source: load,
    closText: closNames.join(', '),
    status: R.prop(GC.FIELD_STATUS, load),
    branchGuid: G.getBranchGuidFromObject(load),
    companyName: R.prop(GC.FIELD_CUSTOMER_NAME, load)
      || R.prop(GC.FIELD_COMPANY_NAME, load)
      || R.path(['customer', GC.FIELD_NAME], load)
      || closNames.join(', '),
    driverCardColor: R.prop(GC.FIELD_DRIVER_CARD_COLOR, load),
    events: normalizeTimelineEvents(load, id, startAt, endAt),
    selectedRateGuid: R.prop(GC.FIELD_SELECTED_RATE_GUID, load),
    rateCurrency: R.path([GC.FIELD_RATE, GC.FIELD_CURRENCY], load)
      || R.prop(GC.FIELD_CURRENCY, load),
    transportationMode: getDropdownGuid(R.prop(GC.FIELD_MODE, load)),
    serviceType: getDropdownGuid(R.prop(GC.FIELD_SERVICE_TYPE, load)),
    itemsCount: R.pathOr(0, [GC.FIELD_ITEMS_INFO, GC.FIELD_COUNT], load),
    secondaryReferenceValues: R.propOr([], 'secondaryReferenceValues', load),
    pickupCount: R.pathOr(1, [GC.FIELD_EVENTS_INFO, GC.FIELD_PICKUP_COUNT], load),
    deliveryCount: R.pathOr(1, [GC.FIELD_EVENTS_INFO, GC.FIELD_DROP_COUNT], load),
    pickupLocation: R.path([GC.SYSTEM_OBJECT_FIRST_EVENT, GC.FIELD_LOCATION], load),
    deliveryLocation: R.path([GC.SYSTEM_OBJECT_LAST_EVENT, GC.FIELD_LOCATION], load),
  };
};

const getDriverName = (driver) => {
  const { firstLastMiddleNames, fullText, loginId } = G.getUserInfo(driver);

  return firstLastMiddleNames || fullText || loginId || '';
};

const getRateAmount = (value) => G.mathRoundNumber(Number(value) || 0, 1);

const getRateSummary = (resource) => {
  const grossCurrency = G.getCurrencySymbol(R.prop('customerRatesTotalCurrency', resource));
  const tripCurrency = G.getCurrencySymbol(R.prop('telRatesTotalCurrency', resource));
  const gross = getRateAmount(R.prop('customerRatesTotal', resource));
  const tripRates = getRateAmount(R.prop('telRatesTotal', resource));
  const tripRatesLabel = G.getWindowLocale('titles:trip-rates', 'Trip Rates');

  return `G: ${grossCurrency}${gross}, ${tripRatesLabel}: ${tripCurrency}${tripRates}`;
};

const getTrailerText = (resource) => {
  const trailerUnitIds = R.pathOr([], [GC.FIELD_TRAILER_UNIT_IDS], resource);
  const trailerUnits = R.pathOr([], [GC.FIELD_TRAILERS], resource).map(({ unitId }) => unitId);

  return [...trailerUnitIds, ...trailerUnits].filter(Boolean).join(', ');
};

const normalizeResourceScheduleItem = ({
  item,
  kind,
  driver,
  groupGuid,
  truckGuid,
  primaryDriver,
  dispatcherGuid,
}) => {
  const driverGuid = G.getGuidFromObject(driver);

  const sourceId = G.getGuidFromObject(item)
    || `${driverGuid}:${R.prop(GC.FIELD_START_DATE, item)}:${R.prop(GC.FIELD_END_DATE, item)}`;

  return {
    kind,
    driver,
    groupGuid,
    truckGuid,
    driverGuid,
    source: item,
    primaryDriver,
    dispatcherGuid,
    id: `${kind}:${sourceId}`,
    driverName: getDriverName(driver),
    endAt: R.prop(GC.FIELD_END_DATE, item),
    startAt: R.prop(GC.FIELD_START_DATE, item),
    driverCardColor: R.prop(GC.FIELD_DRIVER_CARD_COLOR, item),
  };
};

const getResourceScheduleItems = ({
  resource,
  truckMode,
  truckGuid,
  groupGuid,
  primaryDriver,
  dispatcherGuid,
  secondaryDriver,
}) => {
  const definitions = truckMode ? [
    {
      primaryDriver: true,
      driver: primaryDriver,
      pendingActivities: R.propOr([], 'primaryDriverPendingActivities', resource),
      unavailablePeriods: R.propOr([], 'primaryDriverUnavailablePeriods', resource),
    },
    {
      primaryDriver: false,
      driver: secondaryDriver,
      pendingActivities: R.propOr([], 'secondaryDriverPendingActivities', resource),
      unavailablePeriods: R.propOr([], 'secondaryDriverUnavailablePeriods', resource),
    },
  ] : [
    {
      primaryDriver: true,
      driver: primaryDriver,
      pendingActivities: R.propOr([], 'pendingActivities', resource),
      unavailablePeriods: R.propOr([], 'unavailablePeriods', resource),
    },
    {
      primaryDriver: false,
      driver: secondaryDriver,
      pendingActivities: R.pathOr([], ['pendingActivities'], secondaryDriver),
      unavailablePeriods: R.pathOr([], ['unavailablePeriods'], secondaryDriver),
    },
  ];

  return definitions.flatMap((definition) => [
    ...definition.pendingActivities.map((item) => normalizeResourceScheduleItem({
      item,
      groupGuid,
      truckGuid,
      dispatcherGuid,
      driver: definition.driver,
      kind: ENUMS.ENUM_PENDING_ACTIVITY,
      primaryDriver: definition.primaryDriver,
    })),
    ...definition.unavailablePeriods.map((item) => normalizeResourceScheduleItem({
      item,
      groupGuid,
      truckGuid,
      dispatcherGuid,
      driver: definition.driver,
      kind: ENUMS.ENUM_UNAVAILABLE_PERIOD,
      primaryDriver: definition.primaryDriver,
    })),
  ]).filter(({ endAt, startAt, driverGuid }) => endAt && startAt && driverGuid);
};

const normalizeAssignedResource = (resource, resourceMode, resourceGroupBy) => {
  const truckMode = R.equals(resourceMode, RESOURCE_MODE_TRUCK);
  const unitId = truckMode ? R.prop(GC.FIELD_UNIT_ID, resource) : R.prop(GC.FIELD_FLEET_TRUCK_UNIT_ID, resource);
  const primaryDriver = truckMode ? R.prop(GC.FIELD_PRIMARY_DRIVER, resource) : resource;
  const secondaryDriver = R.prop(GC.FIELD_SECONDARY_DRIVER, resource);
  const displayName = truckMode ? unitId : getDriverName(resource);
  const id = G.getGuidFromObject(resource) || R.prop(GC.FIELD_ID, resource) || `${resourceMode}:${displayName}`;
  const driverNames = [primaryDriver, secondaryDriver].map(getDriverName).filter(Boolean);
  const detailsSource = primaryDriver || resource;
  const primaryDriverGuid = G.getGuidFromObject(primaryDriver);
  const secondaryDriverGuid = G.getGuidFromObject(secondaryDriver);
  const groupByGuid = R.prop('groupByGuid', resource);

  const groupGuid = R.prop('groupGuid', resource)
    || G.getGuidFromObject(R.prop(GC.FIELD_GROUP, resource))
    || (
      resourceGroupBy === ENUMS.ENUM_DISPATCHING_GROUP
      || resourceGroupBy === ENUMS.ENUM_GROUP_AND_DISPATCHER
        ? groupByGuid
        : null
    );

  const dispatcherGuid = R.prop('dispatcherGuid', resource)
    || R.prop('assignedDispatcherGuid', resource)
    || G.getGuidFromObject(R.prop('dispatcher', resource))
    || (resourceGroupBy === ENUMS.ENUM_DISPATCHER ? groupByGuid : null);

  const truckGuid = truckMode ? G.getGuidFromObject(resource) : R.prop(GC.FIELD_TRUCK_GUID, resource);

  const trailerGuids = R.prop(GC.FIELD_TRAILER_GUIDS, resource)
    || R.pathOr([], [GC.FIELD_TRAILERS], resource).map(G.getGuidFromObject);

  const loads = R.pathOr([], [GC.SYSTEM_LIST_TELS], resource)
    .map(normalizeLoad)
    .filter(({ endAt, startAt }) => endAt && startAt);

  const scheduleItems = getResourceScheduleItems({
    resource,
    truckMode,
    truckGuid,
    groupGuid,
    primaryDriver,
    dispatcherGuid,
    secondaryDriver,
  });

  return {
    id,
    loads,
    unitId,
    groupGuid,
    displayName,
    driverNames,
    assignment: {
      truckGuid,
      trailerGuids,
      primaryDriverGuid,
      secondaryDriverGuid,
    },
    primaryDriver,
    scheduleItems,
    dispatcherGuid,
    secondaryDriver,
    source: resource,
    type: resourceMode,
    trailerText: getTrailerText(resource),
    rateSummary: getRateSummary(detailsSource),
    secondaryDriverName: getDriverName(secondaryDriver),
    avatarText: G.getUserInfo(primaryDriver).avatarText,
    lastKnownLocation: R.prop('lastKnownLocation', resource)
      || R.prop('lastKnownLocation', detailsSource),
    divisionName: R.prop('assignedToDivisionName', resource),
    phoneNumber: R.prop(GC.FIELD_PHONE_NUMBER, detailsSource),
    domicileTerminal: R.prop(GC.FIELD_DOMICILE_TERMINAL, detailsSource),
  };
};

export const getAssignedResourcesFromResponse = (data, resourceMode, resourceGroupBy) => {
  const resources = R.equals(resourceMode, RESOURCE_MODE_TRUCK)
    ? getArrayPayload(data)
    : (() => {
      const driverLoads = R.path([0, 'driverLoads'], data);

      return Array.isArray(driverLoads) ? driverLoads : getArrayPayload(data);
    })();

  const normalizedResources = resources.map((resource) => (
    normalizeAssignedResource(resource, resourceMode, resourceGroupBy)
  ));

  return [...new Map(normalizedResources.map((resource) => [resource.id, resource])).values()];
};

export const getUnassignedLoadsFromResponse = (data) => getArrayPayload(data)
  .map(normalizeLoad)
  .filter(({ endAt, startAt }) => endAt && startAt);

const shiftDraftEventDate = (value, delta, fallback) => {
  const epoch = new Date(value).getTime();
  const shifted = Number.isFinite(epoch) ? epoch + delta : fallback;

  return Number.isFinite(shifted)
    ? G.convertInstanceToDefaultDateTimeFormat(shifted)
    : value;
};

export const getDraftAvailabilityEvents = (load) => (load.events || []).map((event) => {
  const source = event.source || {};
  const at = new Date(event.at).getTime();
  const sourceAt = new Date(getTimelineEventDate(source, event.kind)).getTime();
  const delta = Number.isFinite(at) && Number.isFinite(sourceAt) ? at - sourceAt : 0;

  const eventLateDate = shiftDraftEventDate(
    source[GC.FIELD_LOAD_EVENT_LATE_DATE] || source.lateDate,
    delta,
    at,
  );

  const eventEarlyDate = shiftDraftEventDate(
    source[GC.FIELD_LOAD_EVENT_EARLY_DATE] || source.earlyDate,
    delta,
    at,
  );

  return {
    ...source,
    [GC.FIELD_LATE_DATE]: eventLateDate,
    [GC.FIELD_EARLY_DATE]: eventEarlyDate,
    [GC.FIELD_LOAD_EVENT_LATE_DATE]: eventLateDate,
    [GC.FIELD_LOAD_EVENT_EARLY_DATE]: eventEarlyDate,
  };
});

const isCloEvent = (event) => event && (
  G.isLoadTypeClo(event) || event.stopType === GC.LOAD_TYPE_CLO
);

export const getCloEventGuids = (events = []) => [...new Set(events
  .filter(isCloEvent)
  .map(G.getGuidFromObject)
  .filter(Boolean))];

export const getEventReferencesByGuid = (data) => {
  const references = Array.isArray(data)
    ? data
    : [data && data.results, data && data.elements].find(Array.isArray)
      || (data && data.eventGuid ? [data] : null);

  if (!references) return null;

  return references.reduce((byEventGuid, reference) => {
    if (!reference || !reference.eventGuid) return byEventGuid;

    const eventReferences = byEventGuid[reference.eventGuid] || [];

    return {
      ...byEventGuid,
      [reference.eventGuid]: [...eventReferences, reference],
    };
  }, {});
};

export const withEventReferences = (event, referencesByEventGuid = {}) => {
  if (!event) return event;

  const eventGuid = G.getGuidFromObject(event);
  let references = Array.isArray(event.references) ? event.references : [];

  if (isCloEvent(event) && eventGuid) references = referencesByEventGuid[eventGuid] || [];

  return { ...event, [GC.FIELD_REFERENCES]: references };
};

const getDriverCardEventUpdate = (event = {}, source = {}, referencesByEventGuid = {}) => ({
  ...withEventReferences(source, referencesByEventGuid),
  location: event.location || source.location,
  guid: G.getGuidFromObject(source) || event.id,
});

const RATE_IS_NEW_FIELD = 'isNew';

const getDriverCardRateUpdate = (rate, telGuid) => {
  const fleetAssignmentFields = R.pick(GC.GROUPED_FIELDS.FLEET_ASSIGNMENT_PICK_ARR, rate);
  const rateFleetAssignment = R.pathOr({}, [GC.FIELD_FLEET_ASSIGNMENT], rate);

  const fleetAssignment = R.omit(
    GC.SYSTEM_LIST_TRAILERS,
    R.mergeRight(rateFleetAssignment, fleetAssignmentFields),
  );

  const rateWithoutAssignmentFields = R.omit([
    GC.FIELD_GUID,
    GC.FIELD_FLEET_ASSIGNMENT,
    RATE_IS_NEW_FIELD,
    ...GC.GROUPED_FIELDS.FLEET_ASSIGNMENT_PICK_ARR,
  ], rate);

  return {
    ...rateWithoutAssignmentFields,
    [GC.FIELD_TEL_GUID]: telGuid,
    [GC.FIELD_FLEET_ASSIGNMENT]: fleetAssignment,
  };
};

export const getDriverCardsUpdateRequest = (enterpriseGuid, editSession = {}, referencesByEventGuid = {}) => {
  const draftLoads = editSession.draftLoads || [];
  const baselineLoads = editSession.baselineLoads || [];
  const draftTelGuids = new Set(draftLoads.map(getTimelineLoadTelGuid).filter(Boolean));

  return {
    enterpriseGuid,
    tels: draftLoads.map((load) => {
      const telGuid = getTimelineLoadTelGuid(load);

      const update = {
        [GC.FIELD_GUID]: telGuid,
        ...(load.rate ? {
          rate: getDriverCardRateUpdate(load.rate, telGuid),
        } : {}),
      };

      if (!isRouteBuilderLoad(load)) {
        update.events = getDraftAvailabilityEvents(load).map((source, index) => (
          getDriverCardEventUpdate((load.events || [])[index], source, referencesByEventGuid)
        ));
      }

      return update;
    }),
    unassignedTelGuids: baselineLoads
      .map(getTimelineLoadTelGuid)
      .filter((telGuid) => telGuid && !draftTelGuids.has(telGuid)),
  };
};

const createZeroMainCharge = (currency) => G.addOneMainChargeToCharges([]).map((charge) => ({
  ...charge,
  [GC.FIELD_CHARGE_CURRENCY]: currency,
  [GC.FIELD_CHARGE_RATE_UNIT]: GC.CHARGE_RATE_UNIT_TYPE_FLAT,
}));

export const createZeroDriverRateDraft = (load, resource, rateFallback = {}) => {
  const assignment = resource.assignment || {};
  const telGuid = getTimelineLoadTelGuid(load);

  const currency = load.rateCurrency
    || G.getAmousConfigByNameFromWindow(GC.GENERAL_BRANCH_DEFAULT_CURRENCY);

  const primaryDriverCharges = createZeroMainCharge(currency);

  const secondaryDriverCharges = assignment.secondaryDriverGuid
    ? createZeroMainCharge(currency)
    : null;

  return {
    currency,
    selected: true,
    customerTotal: 0,
    primaryDriverCharges,
    secondaryDriverCharges,
    fleetAssignment: assignment,
    [GC.FIELD_TEL_GUID]: telGuid,
    serviceType: load.serviceType,
    truckGuid: assignment.truckGuid,
    trailerGuids: assignment.trailerGuids,
    transportationMode: load.transportationMode,
    primaryDriverGuid: assignment.primaryDriverGuid,
    secondaryDriverGuid: assignment.secondaryDriverGuid,
    [GC.FIELD_TOTAL_TRIP_WEIGHT]: rateFallback[GC.FIELD_TOTAL_TRIP_WEIGHT],
    [GC.FIELD_TOTAL_TRIP_WEIGHT_UOM]: rateFallback[GC.FIELD_TOTAL_TRIP_WEIGHT_UOM],
  };
};
